import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Package,
    TrendingUp,
    Settings,
    DollarSign,
    BarChart3,
    ClipboardList,
    Users,
    FileText,
    BookOpen,
    PackageCheck,
    Boxes,
    Receipt,
    NotebookPen,
    ChartPie,
    User,
    ShoppingCart,
    Handshake,
    GitBranch,
    Truck,
    ChevronDown,
    ChevronUp,
    X,
    Search,
    Filter,
    Grid3X3,
    Layers,
    ArrowRight,
    CreditCard,
    Banknote,
    Sheet,
    Calculator,
    LayoutTemplate,
    Wallet,
    CalendarDays,
    CalendarCheck2,
    PiggyBank
} from 'lucide-react';
import authUtils from '../utils/authUtils';

const Home = () => {
    const navigate = useNavigate();
    const [showFlowchart, setShowFlowchart] = useState(false);
    const [flowchartImage, setFlowchartImage] = useState('');
    const [flowchartTitle, setFlowchartTitle] = useState('');
    const [imageLoading, setImageLoading] = useState(false);

    // States cho search và filter
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [displayMode, setDisplayMode] = useState(() => localStorage.getItem('menuDisplayMode') || 'grouped');
    const [showFilters, setShowFilters] = useState(false);

    // State cho collapse groups
    const [collapsedGroups, setCollapsedGroups] = useState(() => {
        const saved = localStorage.getItem('collapsedGroups');
        return saved ? new Set(JSON.parse(saved)) : new Set();
    });

    const openFlowchart = useCallback((imagePath, title) => {
        setFlowchartImage(imagePath);
        setFlowchartTitle(title);
        setShowFlowchart(true);
        setImageLoading(true);
    }, []);

    const closeFlowchart = useCallback(() => {
        setShowFlowchart(false);
        setFlowchartImage('');
        setFlowchartTitle('');
        setImageLoading(false);
    }, []);

    // Handle ESC key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && showFlowchart) {
                closeFlowchart();
            }
        };

        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [showFlowchart, closeFlowchart]);

    const toggleGroup = useCallback((categoryIndex) => {
        setCollapsedGroups(prev => {
            const newCollapsed = new Set(prev);
            if (newCollapsed.has(categoryIndex)) {
                newCollapsed.delete(categoryIndex);
            } else {
                newCollapsed.add(categoryIndex);
            }
            localStorage.setItem('collapsedGroups', JSON.stringify([...newCollapsed]));
            return newCollapsed;
        });
    }, []);

    const handleDisplayModeChange = useCallback((mode) => {
        setDisplayMode(mode);
        localStorage.setItem('menuDisplayMode', mode);
    }, []);

    const clearSearch = useCallback(() => {
        setSearchQuery('');
    }, []);

    const menuCategories = [
        {
            title: '1. QUẢN LÝ KHO',
            icon: Package,
            gradient: 'from-pink-500 to-pink-600',
            bgGradient: 'from-pink-100 to-rose-100',
            hoverGradient: 'hover:from-pink-200 hover:to-rose-200',
            flowchart: {
                path: '/Quan ly kho HNL.png',
                title: 'Lưu đồ Quản lý Kho'
            },
            items: [
                {
                    title: 'Cài đặt giá bán',
                    icon: DollarSign,
                    path: '/giaban',
                    gradient: 'from-pink-400 to-pink-500'
                },
                {
                    title: 'Danh mục hàng hóa',
                    icon: Boxes,
                    path: '/dmhh',
                    gradient: 'from-rose-400 to-rose-500'
                },
                {
                    title: 'Quản lý xuất nhập kho',
                    icon: ClipboardList,
                    path: '/xuatnhapkho',
                    gradient: 'from-pink-500 to-pink-600'
                },
                {
                    title: 'Chi tiết xuất nhập kho',
                    icon: FileText,
                    path: '/chitietxuatnhapkho',
                    gradient: 'from-pink-600 to-pink-700'
                },
                {
                    title: 'Quản lý xuất nhập kho Domino - 18',
                    icon: Boxes,
                    path: '/xuatnhapkhodomino',
                    gradient: 'from-pink-500 to-pink-600'
                },

                {
                    title: 'Báo cáo kho',
                    icon: BarChart3,
                    path: '/tonkho',
                    gradient: 'from-rose-500 to-rose-600'
                },
                {
                    title: 'Mã kiện',
                    icon: PackageCheck,
                    path: '/makien',
                    gradient: 'from-pink-600 to-pink-700'
                },
                {
                    title: 'Duyệt yêu cầu',
                    icon: ClipboardList,
                    path: '/duyet_sua_phieu',
                    gradient: 'from-pink-500 to-pink-600'
                }
            ]
        },
        {
            title: '2. QUẢN LÝ NĂNG SUẤT',
            icon: TrendingUp,
            gradient: 'from-teal-500 to-teal-600',
            bgGradient: 'from-teal-100 to-cyan-100',
            hoverGradient: 'hover:from-teal-200 hover:to-cyan-200',
            flowchart: {
                path: '/Quan ly nang suat HNL.png',
                title: 'Lưu đồ Quản lý Năng suất'
            },
            items: [
                {
                    title: 'Công đoạn - Đơn giá',
                    icon: Receipt,
                    path: '/congdoan_dongia',
                    gradient: 'from-teal-400 to-teal-500'
                },
                {
                    title: 'Phân bổ nhân sự',
                    icon: Users,
                    path: '/phanbonhansu',
                    gradient: 'from-cyan-400 to-cyan-500'
                },
                {
                    title: 'Nhập - Phân bổ bao bì',
                    icon: NotebookPen,
                    path: '/nhapbaobi',
                    gradient: 'from-teal-500 to-teal-600'
                },
                {
                    title: 'Chi tiết phân bổ',
                    icon: ClipboardList,
                    path: '/chitiet_phanbo',
                    gradient: 'from-cyan-500 to-cyan-600'
                },
                {
                    title: 'Tổng hợp năng suất',
                    icon: ChartPie,
                    path: '/baocao_nangsuat',
                    gradient: 'from-cyan-500 to-cyan-600'
                },
                // {
                //     title: 'Tổng hợp năng suất',
                //     icon: ChartPie,
                //     path: '/tonghop',
                //     gradient: 'from-teal-600 to-teal-700'
                // }
            ]
        },
        {
            title: '3. QUẢN LÝ CÔNG LƯƠNG',
            icon: Banknote,
            gradient: 'from-emerald-500 to-emerald-600',
            bgGradient: 'from-emerald-100 to-green-100',
            hoverGradient: 'hover:from-emerald-200 hover:to-green-200',
            items: [
                {
                    title: 'Bảng công lương',
                    icon: Sheet,
                    path: '/bangcongluong',
                    gradient: 'from-emerald-500 to-emerald-600'
                },
                {
                    title: 'Cấu hình công lương',
                    icon: Calculator,
                    path: '/cauhinh_congluong',
                    gradient: 'from-green-400 to-green-500'
                },
                {
                    title: 'Mẫu bảng lương',
                    icon: LayoutTemplate,
                    path: '/maubangluong',
                    gradient: 'from-emerald-400 to-emerald-500'
                },
                {
                    title: 'Thỏa thuận lương',
                    icon: Handshake,
                    path: '/thoathuan_congluong',
                    gradient: 'from-green-500 to-green-600'
                },
                {
                    title: 'Phiếu ứng lương',
                    icon: Wallet,
                    path: '/ungluong',
                    gradient: 'from-emerald-600 to-emerald-700'
                },
                {
                    title: 'Lịch ngày lễ',
                    icon: CalendarDays,
                    path: '/lichngayle',
                    gradient: 'from-teal-500 to-teal-600'
                },
                {
                    title: 'Phép năm',
                    icon: CalendarCheck2,
                    path: '/phepnam',
                    gradient: 'from-teal-600 to-teal-700'
                },
                {
                    title: 'Sổ tiền giữ lại',
                    icon: PiggyBank,
                    path: '/sotiengiulai',
                    gradient: 'from-emerald-500 to-teal-600'
                },
                {
                    title: 'Báo cáo công lương',
                    icon: BarChart3,
                    path: '/baocao_congluong',
                    gradient: 'from-green-600 to-green-700'
                }
            ]
        },
        {
            title: '4. QUẢN LÝ BÁN HÀNG',
            icon: ShoppingCart,
            gradient: 'from-indigo-500 to-indigo-600',
            bgGradient: 'from-indigo-100 to-purple-100',
            hoverGradient: 'hover:from-indigo-200 hover:to-purple-200',
            flowchart: {
                path: '/Quan ly ban hang HNL.png',
                title: 'Lưu đồ Quản lý Bán hàng'
            },
            items: [
                {
                    title: 'Danh sách khách hàng',
                    icon: Users,
                    path: '/dskh',
                    gradient: 'from-indigo-400 to-indigo-500'
                },
                {
                    title: 'Hợp đồng nguyên tắc',
                    icon: Handshake,
                    path: '/hopdongnguyentac',
                    gradient: 'from-purple-500 to-purple-600'
                },
                {
                    title: 'Báo cáo bán hàng',
                    icon: BarChart3,
                    path: '/baocaobanhang',
                    gradient: 'from-indigo-600 to-indigo-700'
                },
                {
                    title: 'Quản lý thanh toán',
                    icon: CreditCard,
                    path: '/thanhtoan',
                    gradient: 'from-purple-600 to-purple-700'
                },
                {
                    title: 'Quản lý công nợ',
                    icon: DollarSign,
                    path: '/congno',
                    gradient: 'from-indigo-500 to-indigo-600'
                }
            ]
        },
        {
            title: '5. QUẢN LÝ LÔ',
            icon: Boxes,
            gradient: 'from-orange-500 to-orange-600',
            bgGradient: 'from-orange-100 to-amber-100',
            hoverGradient: 'hover:from-orange-200 hover:to-amber-200',
            flowchart: {
                path: '/Quan ly lo HNL.png',
                title: 'Lưu đồ Quản lý Lô'
            },
            items: [
                {
                    title: 'Quản lý lô',
                    icon: Boxes,
                    path: '/quanlylo',
                    gradient: 'from-orange-400 to-orange-500'
                },
                {
                    title: 'Quản lý thu chi theo lô',
                    icon: DollarSign,
                    path: '/thuchi',
                    gradient: 'from-amber-500 to-amber-600'
                },
            ]
        },
        {
            title: '6. QUẢN LÝ PHỤ PHẨM',
            icon: Package,
            gradient: 'from-teal-500 to-teal-600',
            bgGradient: 'from-teal-100 to-cyan-100',
            hoverGradient: 'hover:from-teal-200 hover:to-cyan-200',
            items: [
                {
                    title: 'Quản lý phụ phẩm',
                    icon: Package,
                    path: '/phupham',
                    gradient: 'from-teal-400 to-teal-500'
                },
                {
                    title: 'Quản lý xuất phụ phẩm',
                    icon: Truck,
                    path: '/xuatphupham',
                    gradient: 'from-cyan-500 to-cyan-600'
                },
                {
                    title: 'Xuất phụ phẩm chi tiết',
                    icon: FileText,
                    path: '/chitietxuatphupham',
                    gradient: 'from-teal-600 to-teal-700'
                },
            ]
        },
        {
            title: '7. CHỨC NĂNG KHÁC',
            icon: Settings,
            gradient: 'from-violet-500 to-violet-600',
            bgGradient: 'from-violet-100 to-purple-100',
            hoverGradient: 'hover:from-violet-200 hover:to-purple-200',
            items: [
                {
                    title: 'Quản lý người dùng',
                    icon: User,
                    path: '/users',
                    gradient: 'from-violet-400 to-violet-500'
                },
                {
                    title: 'Quản lý nhân sự',
                    icon: Users,
                    path: '/nhansu',
                    gradient: 'from-purple-500 to-purple-600'
                },
                {
                    title: 'Hướng dẫn sử dụng',
                    icon: BookOpen,
                    path: '/huongdansudung',
                    gradient: 'from-violet-600 to-violet-700'
                }
            ]
        }
    ];

    // Filter logic
    const filteredCategories = menuCategories
        .map((category, index) => {
            // Lọc items theo quyền xem trước
            const permissionFilteredItems = authUtils.filterMenuByPermission(category.items);

            // Sau đó lọc theo search query
            const filteredItems = permissionFilteredItems.filter(item => {
                if (searchQuery === '') return true;
                const searchLower = searchQuery.toLowerCase();
                return item.title.toLowerCase().includes(searchLower);
            });

            return {
                ...category,
                originalIndex: index,
                filteredItems
            };
        })
        .filter(category => {
            if (selectedCategory === 'all') return category.filteredItems.length > 0;
            return category.originalIndex.toString() === selectedCategory && category.filteredItems.length > 0;
        });

    // Get all items for "all" display mode
    const getAllItems = () => {
        const allItems = [];
        filteredCategories.forEach(category => {
            category.filteredItems.forEach(item => {
                allItems.push({
                    ...item,
                    categoryName: category.title,
                    categoryGradient: category.gradient,
                    categoryIndex: category.originalIndex
                });
            });
        });
        return allItems;
    };

    const totalItemsShown = filteredCategories.reduce((total, cat) => total + cat.filteredItems.length, 0);

    const renderAppCard = (item, index, categoryGradient, categoryIndex) => {
        const Icon = item.icon;

        return (
            <div
                key={`${item.path}-${index}`}
                className="group select-none"
            >
                <div
                    className="relative p-3 rounded-2xl bg-white/80 backdrop-blur-sm border border-gray-200/50 hover:border-blue-300/50 hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300 flex flex-col items-center cursor-pointer hover:bg-white hover:-translate-y-1"
                    onClick={() => navigate(item.path)}
                >
                    <div className={`relative w-12 h-12 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center mb-3 transform transition-all duration-300 group-hover:scale-110 shadow-lg group-hover:shadow-xl`}>
                        <Icon className="h-6 w-6 text-white drop-shadow-sm" />
                        <div className="absolute inset-0 rounded-xl bg-gradient-to-tr from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>

                    <div className="w-full text-center">
                        <h4 className="text-xs font-semibold text-gray-800 leading-tight line-clamp-2 mb-1 group-hover:text-blue-600 transition-colors">
                            {item.title}
                        </h4>
                    </div>

                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/30">
            {/* Modern Header with Glass Effect */}
            <div className="sticky top-0 z-10 backdrop-blur-xl bg-white/80 border-b border-gray-200/50 shadow-lg shadow-gray-500/5">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex items-center space-x-4">
                        <div className="flex-1 relative">
                            <div className="relative">
                                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Tìm kiếm chức năng..."
                                    className="w-full pl-12 pr-12 py-3 bg-gray-50/50 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400/50 focus:bg-white/90 transition-all duration-300 text-gray-700 placeholder-gray-500 shadow-inner"
                                />
                                {searchQuery && (
                                    <button
                                        onClick={clearSearch}
                                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg"
                                        type="button"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center space-x-2">
                            <div className="flex items-center bg-gray-100/80 backdrop-blur-sm rounded-xl border border-gray-200/50 p-1 shadow-inner">
                                <button
                                    onClick={() => handleDisplayModeChange('grouped')}
                                    className={`p-2 rounded-lg transition-all duration-300 ${displayMode === 'grouped'
                                        ? 'bg-white text-blue-600 shadow-md shadow-blue-500/20'
                                        : 'text-gray-600 hover:bg-white/50 hover:text-gray-800'
                                        }`}
                                    type="button"
                                    title="Theo nhóm"
                                >
                                    <Layers className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() => handleDisplayModeChange('all')}
                                    className={`p-2 rounded-lg transition-all duration-300 ${displayMode === 'all'
                                        ? 'bg-white text-blue-600 shadow-md shadow-blue-500/20'
                                        : 'text-gray-600 hover:bg-white/50 hover:text-gray-800'
                                        }`}
                                    type="button"
                                    title="Hiển thị tất cả"
                                >
                                    <Grid3X3 className="h-4 w-4" />
                                </button>
                            </div>

                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`p-2 rounded-xl transition-all duration-300 border ${showFilters
                                    ? 'bg-blue-500 text-white border-blue-500 shadow-md shadow-blue-500/25'
                                    : 'bg-white/80 text-gray-600 hover:bg-white hover:text-gray-800 border-gray-200/50 hover:shadow-md'
                                    }`}
                                type="button"
                            >
                                <Filter className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    {showFilters && (
                        <div className="mt-4 p-4 bg-white/60 backdrop-blur-sm rounded-xl border border-gray-200/50 shadow-lg">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                    <select
                                        value={selectedCategory}
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                        className="px-3 py-2 bg-white/80 backdrop-blur-sm border border-gray-200/50 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400/50 transition-all shadow-sm text-sm"
                                    >
                                        <option value="all">Tất cả danh mục</option>
                                        {menuCategories.map((cat, idx) => (
                                            <option key={idx} value={idx.toString()}>
                                                {cat.title}
                                            </option>
                                        ))}
                                    </select>

                                    <div className="flex items-center space-x-3 text-sm">
                                        <div className="flex items-center space-x-2">
                                            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                                            <span className="text-gray-600">
                                                <span className="font-semibold text-blue-600">
                                                    {displayMode === 'grouped' ? 'Theo nhóm' : 'Tất cả'}
                                                </span>
                                            </span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                                            <span className="text-gray-600">
                                                <span className="font-semibold text-emerald-600">{totalItemsShown}</span> chức năng
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={() => setShowFilters(false)}
                                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-lg transition-colors"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-6 py-6">
                {displayMode === 'all' ? (
                    <div>
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-md">
                                    <Grid3X3 className="h-4 w-4 text-white" />
                                </div>
                                <h2 className="text-lg font-bold text-gray-800">
                                    Tất cả chức năng
                                </h2>
                            </div>
                            <div className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-3 py-1 rounded-xl border border-gray-200/50 shadow-md">
                                <TrendingUp className="h-3 w-3 text-emerald-500" />
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold text-emerald-600">{totalItemsShown}</span> chức năng
                                </span>
                            </div>
                        </div>

                        {totalItemsShown === 0 ? (
                            <div className="text-center py-16">
                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center mx-auto mb-4 shadow-inner">
                                    <Grid3X3 className="h-8 w-8 text-gray-400" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-800 mb-2">Không có chức năng nào</h3>
                                <p className="text-gray-600 text-sm">Thử thay đổi bộ lọc hoặc tìm kiếm</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                                {getAllItems().map((item, index) =>
                                    renderAppCard(item, index, item.categoryGradient, item.categoryIndex)
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    <div>
                        {filteredCategories.map((category) => {
                            const isCollapsed = collapsedGroups.has(category.originalIndex);
                            const CategoryIcon = category.icon;

                            return (
                                <div key={category.originalIndex} className="mb-8">
                                    {/* Category Header */}
                                    <div className={`flex items-center justify-between mb-4 p-4 rounded-xl bg-gradient-to-r ${category.bgGradient} ${category.hoverGradient} transition-all duration-300 shadow-md hover:shadow-lg border border-opacity-20`}>
                                        <div
                                            className="flex items-center flex-1 cursor-pointer group"
                                            onClick={() => toggleGroup(category.originalIndex)}
                                        >
                                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${category.gradient} flex items-center justify-center mr-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                                                <CategoryIcon className="h-6 w-6 text-white" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors uppercase tracking-wide">
                                                    {category.title}
                                                </h3>
                                                <p className="text-xs text-gray-700 mt-1 font-semibold">{category.filteredItems.length} chức năng</p>
                                            </div>
                                            <div className="ml-4 px-3 py-1.5 bg-white/60 text-gray-900 text-xs rounded-full font-bold shadow-sm">
                                                {category.filteredItems.length}
                                            </div>
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            {/* Flowchart Button */}
                                            {category.flowchart && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openFlowchart(category.flowchart.path, category.flowchart.title);
                                                    }}
                                                    className={`flex items-center px-3 py-2 sm:px-4 sm:space-x-2 bg-gradient-to-r ${category.gradient} text-white rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-semibold`}
                                                    type="button"
                                                    title="Xem lưu đồ"
                                                >
                                                    <FileText className="h-4 w-4" />
                                                    <span className="hidden sm:inline ml-2">Xem lưu đồ</span>
                                                </button>
                                            )}

                                            {/* Toggle Button */}
                                            <button
                                                onClick={() => toggleGroup(category.originalIndex)}
                                                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white/40 rounded-lg transition-all duration-200"
                                                type="button"
                                            >
                                                {isCollapsed ? <ChevronDown className="h-5 w-5" /> : <ChevronUp className="h-5 w-5" />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Category Items */}
                                    {!isCollapsed && (
                                        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3 pl-4">
                                            {category.filteredItems.map((item, index) =>
                                                renderAppCard(item, index, category.gradient, category.originalIndex)
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {filteredCategories.length === 0 && (
                            <div className="text-center py-16">
                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center mx-auto mb-4 shadow-inner">
                                    <Search className="h-8 w-8 text-gray-400" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-800 mb-2">Không tìm thấy kết quả</h3>
                                <p className="text-gray-600 text-sm mb-4">Thử từ khóa khác hoặc xóa bộ lọc</p>
                                <button
                                    onClick={clearSearch}
                                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300 text-sm"
                                >
                                    Xóa tìm kiếm
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Enhanced Flowchart Modal */}
            {showFlowchart && (
                <div
                    className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
                    onClick={closeFlowchart}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-md">
                                    <FileText className="h-5 w-5 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900">{flowchartTitle}</h3>
                                    <p className="text-xs text-gray-600">Nhấn ESC hoặc click bên ngoài để đóng</p>
                                </div>
                            </div>
                            <button
                                onClick={closeFlowchart}
                                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-all duration-200"
                                type="button"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-4 overflow-auto max-h-[calc(90vh-80px)] bg-gray-50 relative">
                            {imageLoading && (
                                <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
                                    <div className="text-center">
                                        <div className="w-12 h-12 border-3 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
                                        <p className="text-gray-600 text-sm">Đang tải hình ảnh...</p>
                                    </div>
                                </div>
                            )}
                            <img
                                src={flowchartImage}
                                alt={flowchartTitle}
                                className="w-full h-auto rounded-lg shadow-lg"
                                onLoad={() => setImageLoading(false)}
                                onError={(e) => {
                                    setImageLoading(false);
                                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600"%3E%3Crect width="800" height="600" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="24" fill="%239ca3af"%3EKhông tìm thấy hình ảnh%3C/text%3E%3C/svg%3E';
                                }}
                            />
                        </div>

                        {/* Footer */}
                        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
                            <button
                                onClick={closeFlowchart}
                                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-sm hover:shadow-md"
                            >
                                Đóng
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Floating Action Button */}
            <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="fixed bottom-6 right-6 w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl shadow-lg hover:shadow-xl hover:shadow-blue-500/25 transition-all duration-300 flex items-center justify-center group hover:scale-105 z-40"
                title="Về đầu trang"
            >
                <ArrowRight className="h-5 w-5 rotate-[-90deg] group-hover:translate-y-[-1px] transition-transform" />
            </button>

            {/* Background Pattern */}
            <div className="fixed inset-0 pointer-events-none z-[-1]">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.3)_1px,transparent_0)] bg-[length:32px_32px] opacity-30"></div>
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-50/20 via-transparent to-purple-50/20"></div>
            </div>
        </div>
    );
};

export default Home;
